import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";
import Loader from "../../../components/loader/loader";
import Snackbar from "../../../components/snackbar/snackbar";
import {
  admin_internship_applications_detail,
  admin_internship_application_verify,
  superadmin_internship_applications_detail
} from "../admin.api";
import "../internshipRequests/style.css"; // Reuse existing styles

export default function InternshipApplicationDetail(props) {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  useEffect(() => {
    if (id) {
      fetchDetail();
    }
  }, [id]);

  const fetchDetail = () => {
    setIsLoading(true);
        const adminType = localStorage.getItem("adminType");
        const apiCall =
          adminType === 2
            ? superadmin_internship_applications_detail(id)
            : admin_internship_applications_detail(id)
        
        apiCall
      .then((res) => {
        if (res?.success && res?.data) {
          setData(res.data);
        } else {
          setSnackMessage("Failed to load application details");
          setIsSnackError(true);
          setSnackOpen(true);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching detail:", error);
        setSnackMessage("Error loading application details");
        setIsSnackError(true);
        setSnackOpen(true);
        setIsLoading(false);
      });
  };

  const handleVerify = (status) => {
    let title = "";
    let confirmButtonText = "";
    let confirmButtonColor = "";

    if (status === 1) {
      title = "Approve Application?";
      confirmButtonText = "Yes, Approve";
      confirmButtonColor = "#28a745";
    } else if (status === 2) {
      title = "Revert Application?";
      confirmButtonText = "Yes, Revert";
      confirmButtonColor = "#ffc107";
    } else {
      title = "Reject Application?";
      confirmButtonText = "Yes, Reject";
      confirmButtonColor = "#dc3545";
    }

    Swal.fire({
      title: title,
      text: status === 1 ? "The student will be eligible for rankings." : "Please provide a reason for the student.",
      input: status !== 1 ? "textarea" : null,
      inputPlaceholder: "Type your message here...",
      showCancelButton: true,
      confirmButtonText: confirmButtonText,
      confirmButtonColor: confirmButtonColor,
      inputValidator: (value) => {
        if (status !== 1 && !value) {
          return "You need to write something!";
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setIsUpdating(true);
        const payload = {
          status: status,
          admin_message: typeof result.value === "string" ? result.value : "",
        };

        admin_internship_application_verify(id, payload)
          .then((res) => {
            if (res?.success) {
              setSnackMessage(res.message || "Status updated successfully");
              setIsSnackError(false);
              setSnackOpen(true);
              fetchDetail();
            } else {
              setSnackMessage(res.message || "Failed to update status");
              setIsSnackError(true);
              setSnackOpen(true);
            }
            setIsUpdating(false);
          })
          .catch((error) => {
            setSnackMessage("Error updating status");
            setIsSnackError(true);
            setSnackOpen(true);
            setIsUpdating(false);
          });
      }
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 1:
        return { label: "Approved", color: "success" };
      case 2:
        return { label: "Reverted", color: "warning" };
      case 3:
        return { label: "Rejected", color: "danger" };
      default:
        return { label: "Pending", color: "secondary" };
    }
  };

  const snackbarClose = () => {
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
  };

  if (isLoading) return <Loader open={true} />;

  if (!data) return (
    <div className="container mt-5">
      <div className="alert alert-warning">
        <p>Failed to load application details.</p>
        <button className="btn btn-primary btn-sm" onClick={() => props.history.goBack()}>Go Back</button>
      </div>
    </div>
  );

  const getBaseUrl = () => {
    return process?.env?.REACT_APP_BASE_URL;
  };

  return (
    <div>
      <Loader open={isUpdating} />
      <Snackbar message={snackMessage} error={isSnackError} open={snackOpen} close={snackbarClose} />

      <div className="view-container">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <button
              style={{ backgroundColor: "#0a223d", color: "#fff", border: "solid 1px #0a223d" }}
              type="button"
              className="btn btn-warning btn-sm mr-3"
              onClick={() => props.history.push("/admin-internship-applications")}
            >
              Back
            </button>
            <h6 className="mb-0"><b>Internship Application Detail</b></h6>
          </div>
          {data.status === 0 && (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-success"
                onClick={() => handleVerify(1)}
                disabled={data.status === 'approved'}
              >
                Approve
              </button>
              <button
                className="btn btn-sm btn-warning"
                onClick={() => handleVerify(2)}
                disabled={data.status === 'reverted'}
              >
                Revert
              </button>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => handleVerify(3)}
                disabled={data.status === 'rejected'}
              >
                Reject
              </button>
            </div>
          )}        
        </div>

        <hr />

        <div className="data">
          <div className="row mb-4">
            <div className="col-md-2">
              {data.file_upload_photo ? (
                <img
                  src={`${getBaseUrl()}${data.file_upload_photo}`}
                  alt="Applicant"
                  className="img-fluid rounded border"
                  style={{ maxHeight: '150px' }}
                />
              ) : (
                <div className="bg-light d-flex align-items-center justify-content-center border rounded" style={{ height: '150px' }}>
                  No Photo
                </div>
              )}
            </div>
            <div className="col-md-10">
              <h4 className="text-primary">{data.full_name}</h4>
              <p className="mb-1"><strong>Email:</strong> {data.email}</p>
              <p className="mb-1"><strong>Phone:</strong> {data.phone_no}</p>
              <p className="mb-0">
                <strong>Status:</strong>
                <span className={`badge bg-${getStatusBadge(data.status).color} ml-2`}>
                  {getStatusBadge(data.status).label}
                </span>
              </p>
              {data.admin_remarks && (
                <div className="mt-2 p-2 bg-light border-left border-info small">
                  <strong>Last Admin Message:</strong> {data.admin_remarks}
                </div>
              )}
            </div>
          </div>

          <h5 className="section-title mt-4">Basic Information</h5>
          <div className="row mb-2">
            <div className="col-3">Date of Birth</div>
            <div className="col-1">:</div>
            <div className="col-7">{moment(data.date_of_birth).format("DD-MM-YYYY")}</div>
          </div>
          <div className="row mb-2">
            <div className="col-3">Gender</div>
            <div className="col-1">:</div>
            <div className="col-7">{data.gender}</div>
          </div>
          <div className="row mb-2">
            <div className="col-3">Kerala Native</div>
            <div className="col-1">:</div>
            <div className="col-7">{data.is_kerala_native ? "Yes" : "No"}</div>
          </div>
          <div className="row mb-2">
            <div className="col-3">Course Status</div>
            <div className="col-1">:</div>
            <div className="col-7 text-capitalize">{data.course_completion_status}</div>
          </div>
          <div className="row mb-2">
            <div className="col-3">Address</div>
            <div className="col-1">:</div>
            <div className="col-7">{data.address}</div>
          </div>

          <h5 className="section-title mt-4">Educational Qualifications</h5>
          {data.intern_qualification?.map((qual, idx) => (
            <div key={idx} className="card mb-3 bg-light border-0 shadow-sm">
              <div className="card-body">
                <div className="row mb-2">
                  <div className="col-md-4"><strong>Type:</strong> {qual.qualification_type?.name}</div>
                  <div className="col-md-4"><strong>Qualification:</strong> {qual.qualification?.name}</div>
                  <div className="col-md-4"><strong>Stream:</strong> {qual.stream?.name || "--"}</div>
                </div>
                <div className="row mb-2">
                  <div className="col-md-4"><strong>Board:</strong> {qual.board}</div>
                  <div className="col-md-4"><strong>Year:</strong> {qual.year_passed}</div>
                  <div className="col-md-4"><strong>System:</strong> {qual.mark_system === 'semester_gpa' ? 'Semester GPA' : 'Percentage'}</div>
                </div>

                <h6 className="mt-3">Marks Breakdown</h6>
                <table className="table table-sm table-bordered bg-white">
                  <thead>
                    <tr>
                      <th>Period</th>
                      {qual.mark_system === 'semester_gpa' ? <th>CGPA</th> : <><th>Marks Obtained</th><th>Total Marks</th></>}
                    </tr>
                  </thead>
                  <tbody>
                    {qual.latest_mark?.map((m, mIdx) => (
                      <tr key={mIdx}>
                        <td>{m.period_label}</td>
                        {qual.mark_system === 'semester_gpa' ? <td>{m.cgpa}</td> : <><td>{m.marks_obtained}</td><td>{m.total_marks}</td></>}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {qual.certificate_photo && (
                  <div className="mt-2">
                    <a href={`${getBaseUrl()}${qual.certificate_photo}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-info">
                      View Certificate
                    </a>
                  </div>
                )}
              </div>
            </div>
          ))}

          <h5 className="section-title mt-4">Internship Proposals</h5>
          <div className="list-group mb-4">
            {data.proposals?.map((prop, idx) => (
              <div key={idx} className="list-group-item d-flex justify-content-between align-items-center">
                <div>
                  <h6>#{prop.preference_order} - {prop.internship_request?.internship_discipline}</h6>
                  <p className="mb-0 text-muted">{prop.internship_request?.company?.company_name}</p>
                </div>
                <span className={`badge bg-${prop.status === 'pending' ? 'warning' : 'success'}`}>
                  {prop.status}
                </span>
              </div>
            ))}
          </div>

          <h5 className="section-title mt-4">Documents</h5>
          <div className="row">
            <div className="col-md-4">
              <div className="card text-center p-3 border-0 bg-light">
                <h6>Resume</h6>
                {data.resume ? (
                  <a href={`${getBaseUrl()}${data.resume}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary">Download PDF</a>
                ) : "Not Uploaded"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
